import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["__typename"];
import { Map as ImmutableMap, List } from 'immutable';
import getIn from 'transmute/getIn';
import memoize from 'transmute/memoize';
import ObjectIds from 'customer-data-objects/constants/ObjectIds';
import { DEAL, LINE_ITEM, PRODUCT } from 'customer-data-objects/constants/ObjectTypes';
import DealRecord from 'customer-data-objects/deal/DealRecord';
import LineItemRecord from 'customer-data-objects/lineItem/LineItemRecord';
import ProductRecord from 'customer-data-objects/product/ProductRecord';
import PropertyRecord from 'customer-data-objects/property/PropertyRecord';
// @ts-expect-error getIn incorrectly returns unknown value
export const getDefaultCurrencyCode = getIn(['homeCurrency', 'currencyCode']);

// @ts-expect-error getIn incorrectly returns unknown value
export const getHasMulticurrencyActive = getIn(['isMulticurrencyActive']);

// @ts-expect-error getIn incorrectly return unknown value
export const getIsMulticurrencyActive = getIn(['currency', 'isMulticurrencyActive']);

// @ts-expect-error getIn incorrectly returns unknown value
export const getHomeCurrencyCode = getIn(['currency', 'homeCurrency', 'currencyCode']);

// @ts-expect-error getIn incorrectly returns unknown value
export const getMulticurrencies = getIn(['exchangeRates']);
export const parseUserAttributes = userAttributes => {
  return (userAttributes !== null && userAttributes !== void 0 ? userAttributes : []).reduce((acc, setting) => {
    const key = setting.key;
    try {
      // string attributes come back as ""value"" - need to be de-stringified
      return Object.assign({}, acc, {
        [key]: setting.value && JSON.parse(setting.value)
      });
    } catch (e) {
      return Object.assign({}, acc, {
        [key]: setting.value
      });
    }
  }, {});
};
export function parseSettingsArray(settingsArray) {
  return (settingsArray !== null && settingsArray !== void 0 ? settingsArray : []).reduce((acc, setting) => {
    const key = setting.name;
    try {
      // string attributes come back as ""value"" - need to be de-stringified
      return Object.assign({}, acc, {
        [key]: setting.value && JSON.parse(setting.value)
      });
    } catch (e) {
      return Object.assign({}, acc, {
        [key]: setting.value
      });
    }
  }, {});
}

/**
 * Given a Multicurrencies Array returned from GraphQL with array values being
 * multicurrency properties objects, return a List of objects with key being
 * referencedObject and values being an ImmutableMap of the multicurrency properties
 *
 * @param {Array} multicurrenciesArray
 * @returns {Array} Array of objects
 * { referencedObject: ImmutableMap({ multicurrencyProperties }) }
 */
export function parseMulticurrencies(multicurrenciesArray) {
  return multicurrenciesArray.map(multicurrencies => ({
    referencedObject: new Map(Object.entries(multicurrencies))
  }));
}
export const parseProductProperties = memoize(productPropertiesGql => {
  if (!productPropertiesGql) {
    return new Map();
  }
  return productPropertiesGql.reduce((acc, propertyGroup) => {
    var _propertyGroup$allPro;
    (_propertyGroup$allPro = propertyGroup.allPropertyDefinitions) === null || _propertyGroup$allPro === void 0 || _propertyGroup$allPro.forEach(propertyDef => {
      const property = PropertyRecord.fromJS(propertyDef);
      acc.set(property.name, property);
    });
    return acc;
  }, new Map());
});
export const parseLineItemProperties = memoize(lineItemPropertiesGql => {
  if (!lineItemPropertiesGql) {
    return {};
  }
  return lineItemPropertiesGql.reduce((acc, propertyGroup) => {
    var _propertyGroup$allPro2;
    (_propertyGroup$allPro2 = propertyGroup.allPropertyDefinitions) === null || _propertyGroup$allPro2 === void 0 || _propertyGroup$allPro2.forEach(property => {
      acc[property.name] = PropertyRecord.fromJS(property);
    });
    return acc;
  }, {});
});
export const parseDealProperties = memoize(dealPropertiesGql => {
  if (!dealPropertiesGql) {
    return {};
  }
  return dealPropertiesGql.reduce((acc, propertyGroup) => {
    var _propertyGroup$allPro3;
    (_propertyGroup$allPro3 = propertyGroup.allPropertyDefinitions) === null || _propertyGroup$allPro3 === void 0 || _propertyGroup$allPro3.forEach(property => {
      acc[property.name] = PropertyRecord.fromJS(property);
    });
    return acc;
  }, {});
});

/**
 * @deprecated use parseLineItemProperties or parseDealProperties instead
 */
export const parseProperties = memoize(propertyGroupsGql => {
  if (!propertyGroupsGql) {
    return {};
  }

  // flatten array of property groups into array of property definitions
  const propertiesArray = propertyGroupsGql.reduce((acc, propertyGroup) => {
    acc.push(...propertyGroup.allPropertyDefinitions);
    return acc;
  }, []);
  const propertiesObject = {};
  propertiesArray.forEach(property => {
    propertiesObject[property.name] = PropertyRecord.fromJS(property);
  });
  return propertiesObject;
});
export const parseProducts = products => {
  if (!products) {
    return products;
  }
  return ImmutableMap(Object.assign({}, products, {
    results: List(products.results.map(result => ProductRecord.fromJS(result)))
  }));
};
export function parsePropertyGroups(graphQLPropertyGroups, propertyMappingFunc = property => property) {
  const returnMap = new Map();
  if (!graphQLPropertyGroups) {
    return returnMap;
  }
  const sortedPropertyGroups = graphQLPropertyGroups.slice().sort((a, b) => {
    var _a$displayOrder, _b$displayOrder;
    // propertyGroups without display orders should be sorted to the end
    return ((_a$displayOrder = a.displayOrder) !== null && _a$displayOrder !== void 0 ? _a$displayOrder : Number.MAX_SAFE_INTEGER) - ((_b$displayOrder = b.displayOrder) !== null && _b$displayOrder !== void 0 ? _b$displayOrder : Number.MAX_SAFE_INTEGER);
  });
  return sortedPropertyGroups.reduce((acc, propertyGroup, index) => {
    var _propertyGroup$displa;
    return acc.set(propertyGroup.name, {
      displayName: propertyGroup.label,
      displayOrder: (_propertyGroup$displa = propertyGroup.displayOrder) !== null && _propertyGroup$displa !== void 0 ? _propertyGroup$displa : index,
      name: propertyGroup.name,
      hubspotDefined: propertyGroup.hubspotDefined,
      properties: (propertyGroup.allPropertyDefinitions || []).map(propertyMappingFunc)
    });
  }, returnMap);
}
const RecordType = {
  [DEAL]: DealRecord,
  [LINE_ITEM]: LineItemRecord,
  [PRODUCT]: ProductRecord
};
export function toSubject(objectType, gqlCrmObject = {}) {
  const _ref = gqlCrmObject.defaultProperties || {},
    defaultProperties = _objectWithoutPropertiesLoose(_ref, _excluded);
  const propertiesList = gqlCrmObject.properties || [];
  const allPropertiesList = gqlCrmObject.allProperties || [];
  const favoritePropertiesList = gqlCrmObject.favoriteProperties || [];
  // map all properties arrays to maps of PropertyValueRecords keyed by name
  const properties = [...propertiesList, ...allPropertiesList, ...favoritePropertiesList].filter(({
    value
  }) => value !== null).reduce((acc, {
    name,
    value
  }) => {
    if (name) {
      acc[name] = {
        name,
        value
      };
    }
    return acc;
  }, {});
  return RecordType[objectType].fromJS({
    [ObjectIds[objectType]]: gqlCrmObject.id,
    properties: Object.assign({}, properties, defaultProperties)
  });
}
export function parseProductViews(productViewDataShape) {
  if (!productViewDataShape) {
    return productViewDataShape;
  }
  const {
    total,
    results,
    paging
  } = productViewDataShape;
  return {
    total,
    results: results.map(result => Object.assign({}, result, {
      quickFilters: JSON.parse(result.quickFilters),
      filterGroups: JSON.parse(result.filterGroups)
    })),
    next: paging && paging.next.after
  };
}